import {
  defaultSchedulingDepositPolicy,
  resolveSchedulingDepositPolicy,
  reviewSchedulingDepositPolicy,
  type SchedulingDepositPolicy,
  type SchedulingDepositSettings,
} from '@hue/contracts';
import {
  Button,
  CardHeader,
  CurrencyInput,
  FormErrorSummary,
  Input,
  MultiSelect,
  SegmentedControl,
  useSnackbar,
} from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { HandCoins } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { ErrorCard, LoadingCard, RequestAlert } from '../../app/data-view.js';
import { PageHeader } from '../../app/page-title.js';
import { getApiError, schedulingApi } from '../../lib/api.js';
import { useServiceOptions } from './scheduling-options.js';

const QUERY_KEY = ['company', 'scheduling', 'deposit-settings'] as const;

type Target = { kind: 'unit' | 'service'; id: string; displayName: string };

const COMPANY = 'COMPANY';

/// Sinal do agendamento (ADR 0058): padrão da empresa, configuração própria por unidade e regra
/// própria por serviço, que vence as outras duas. Várias unidades e serviços podem receber a mesma
/// regra de uma vez. O valor e o prazo ficam retratados em cada pedido; mudar aqui vale para os
/// próximos pedidos.
export function CompanyDepositSettings({
  units = [],
  salesContracted,
  children,
}: {
  units?: Array<{ id: string; displayName: string }>;
  salesContracted: boolean;
  children?: ReactNode;
}) {
  const [scope, setScope] = useState<string[]>([COMPANY]);
  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => schedulingApi.depositSettings(),
    retry: false,
  });
  // Sem acesso ao catálogo de serviços, a tela segue só com empresa e unidades.
  const services = useServiceOptions().data?.services ?? [];
  const own = (kind: 'unit' | 'service', id: string) =>
    (kind === 'unit' ? query.data?.units[id] : query.data?.services[id]) ? ' · regra própria' : '';
  // Na ordem da lista, não na dos cliques: unidades primeiro, depois serviços.
  const targets = [
    ...units.map(({ id, displayName }) => ({ kind: 'unit' as const, id, displayName })),
    ...services.map(({ id, displayName }) => ({ kind: 'service' as const, id, displayName })),
  ]
    .filter((item) => scope.includes(`${item.kind}:${item.id}`))
    .map(({ kind, id, displayName }): Target => ({ kind, id, displayName }));
  return (
    <section className="max-w-3xl">
      <PageHeader
        trail="Configurações / Sinal do agendamento"
        title="Sinal do agendamento"
        description="Quanto o cliente paga para a unidade confirmar o horário pedido e até quando. O sinal é recebido na venda do atendimento; pagar não confirma sozinho."
      />
      {children ? <div className="mb-6">{children}</div> : null}
      <div className="grid gap-4">
        {units.length || services.length ? (
          <MultiSelect
            label="Configurar"
            itemNoun="itens"
            noneLabel="Nada escolhido"
            searchPlaceholder="Buscar unidade ou serviço"
            exclusiveValue={COMPANY}
            selected={scope}
            onChange={setScope}
            options={[
              { value: COMPANY, label: 'Padrão da empresa' },
              ...units.map((unit) => ({
                value: `unit:${unit.id}`,
                label: `Unidade: ${unit.displayName}${own('unit', unit.id)}`,
              })),
              ...services.map((service) => ({
                value: `service:${service.id}`,
                label: `Serviço: ${service.displayName}${own('service', service.id)}`,
              })),
            ]}
          />
        ) : null}
        {query.isLoading ? (
          <LoadingCard label="Carregando o sinal do agendamento" />
        ) : query.error ? (
          <ErrorCard message={getApiError(query.error)} />
        ) : !scope.includes(COMPANY) && !targets.length ? (
          <p className="text-sm text-content-muted">
            Escolha o padrão da empresa ou as unidades e os serviços que vão receber a regra.
          </p>
        ) : (
          <DepositPolicyForm
            key={`${scope.join()}:${JSON.stringify(query.data)}`}
            settings={query.data!}
            targets={targets}
            salesContracted={salesContracted}
          />
        )}
      </div>
    </section>
  );
}

function DepositPolicyForm({
  settings,
  targets,
  salesContracted,
}: {
  settings: SchedulingDepositSettings;
  /// Vazio: o padrão da empresa.
  targets: Target[];
  salesContracted: boolean;
}) {
  const client = useQueryClient();
  const snackbar = useSnackbar();
  const [first] = targets;
  const hasOwn = (target: Target) =>
    Boolean((target.kind === 'unit' ? settings.units : settings.services)[target.id]);
  const owned = targets.filter(hasOwn);
  const kinds = new Set(targets.map((target) => target.kind));
  // Com vários alvos, o formulário parte da regra do primeiro e salvar aplica a mesma a todos.
  const [draft, setDraft] = useState<SchedulingDepositPolicy>(
    !first
      ? settings.company
      : first.kind === 'unit'
        ? resolveSchedulingDepositPolicy(settings, first.id)
        : (settings.services[first.id] ?? settings.company),
  );
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const patch = (value: Partial<SchedulingDepositPolicy>) =>
    setDraft((current) => ({ ...current, ...value }));
  const problems = reviewSchedulingDepositPolicy(draft);
  const charges = draft.mode !== 'NONE';

  async function save(next: SchedulingDepositSettings, message: string) {
    setSaving(true);
    setError(undefined);
    try {
      client.setQueryData(QUERY_KEY, await schedulingApi.saveDepositSettings(next));
      snackbar.success(message);
    } catch (reason) {
      setError(getApiError(reason));
    } finally {
      setSaving(false);
    }
  }

  function submit() {
    setSubmitted(true);
    if (problems.length) return;
    const policy: SchedulingDepositPolicy = charges
      ? { ...draft, expiryAction: draft.mode === 'DEADLINE' ? draft.expiryAction : 'KEEP_PENDING' }
      : defaultSchedulingDepositPolicy;
    if (!first) {
      void save({ ...settings, company: policy }, 'Sinal do agendamento salvo.');
      return;
    }
    const pick = (kind: Target['kind']) =>
      Object.fromEntries(
        targets.filter((target) => target.kind === kind).map((target) => [target.id, policy]),
      );
    void save(
      {
        ...settings,
        units: { ...settings.units, ...pick('unit') },
        services: { ...settings.services, ...pick('service') },
      },
      'Sinal do agendamento salvo.',
    );
  }

  function removeOwn() {
    const units = { ...settings.units };
    const services = { ...settings.services };
    for (const target of owned) delete (target.kind === 'unit' ? units : services)[target.id];
    void save(
      { ...settings, units, services },
      owned.length > 1
        ? 'Regras próprias removidas.'
        : owned[0]!.kind === 'unit'
          ? 'Unidade usando o padrão da empresa.'
          : 'Serviço seguindo a regra da unidade.',
    );
  }

  const amountError =
    submitted && charges
      ? problems.find((item) => item.includes('sinal maior') || item.includes('percentual'))
      : undefined;
  const limitError =
    submitted && draft.mode === 'DEADLINE'
      ? problems.find((item) => item.includes('limite'))
      : undefined;
  const hours = (minutes: number | null) => (minutes === null ? '' : String(minutes / 60));
  const minutesOf = (value: string) =>
    value.trim() === '' ? null : Math.round(Number(value.replace(',', '.')) * 60);

  return (
    <form
      id="deposit-policy-form"
      noValidate
      className="grid gap-4 rounded-2xl border border-surface-border bg-surface-card p-4"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <CardHeader
        title={
          !first
            ? 'Padrão da empresa'
            : targets.length === 1
              ? `Sinal em ${first.displayName}`
              : `Sinal em ${String(targets.length)} itens`
        }
        icon={<HandCoins className="size-4" />}
      />
      <p className="text-sm text-content-muted">
        {!first
          ? 'Vale para as unidades e os serviços sem configuração própria.'
          : targets.length > 1
            ? `Salvar aplica a mesma regra a ${targets.map((target) => target.displayName).join(', ')}. O formulário começa pela regra de ${first.displayName}. A regra de um serviço vence a da unidade e a da empresa.`
            : first.kind === 'unit'
              ? owned.length
                ? 'Esta unidade tem configuração própria.'
                : 'Esta unidade usa o padrão da empresa. Salvar cria uma configuração própria.'
              : owned.length
                ? 'Este serviço tem regra própria: ela vence a da unidade e a da empresa.'
                : 'Este serviço segue a regra da unidade. Salvar cria uma regra própria, que vale em todas as unidades.'}
      </p>
      {!salesContracted ? (
        <p className="text-sm text-content-muted">
          Cobrar sinal exige o módulo Vendas, onde o sinal é recebido. Sem ele, só “Sem sinal” pode
          ser salvo.
        </p>
      ) : null}
      <div className="space-y-2">
        <span className="block text-xs font-semibold text-content/70">Como funciona</span>
        <SegmentedControl
          label="Como funciona o sinal"
          value={draft.mode}
          onChange={(mode) => patch({ mode })}
          options={[
            { value: 'NONE', label: 'Sem sinal' },
            { value: 'REQUIRED', label: 'Exigir sinal' },
            { value: 'DEADLINE', label: 'Sinal com prazo' },
          ]}
        />
        <p className="text-xs text-content-faint">
          {draft.mode === 'NONE'
            ? 'A unidade confirma o pedido sem cobrar sinal.'
            : draft.mode === 'REQUIRED'
              ? 'A unidade só confirma o pedido com o sinal pago ou dispensado com motivo.'
              : 'Como “Exigir sinal”, com prazo para pagar. Vence o primeiro limite configurado.'}
        </p>
      </div>
      {charges ? (
        <>
          <div className="space-y-2">
            <span className="block text-xs font-semibold text-content/70">Valor</span>
            <SegmentedControl
              label="Tipo de valor do sinal"
              value={draft.amountMode}
              onChange={(amountMode) => patch({ amountMode, amount: 0 })}
              options={[
                { value: 'FIXED', label: 'Valor fixo' },
                { value: 'PERCENTAGE', label: 'Percentual do serviço' },
              ]}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {draft.amountMode === 'FIXED' ? (
              <CurrencyInput
                label="Valor do sinal"
                value={draft.amount || null}
                onValueChange={(amount) => patch({ amount: amount ?? 0 })}
                hint="Limitado ao preço do serviço."
                error={amountError}
              />
            ) : (
              <Input
                label="Percentual do sinal (%)"
                type="number"
                min={0.01}
                max={100}
                step="0.01"
                value={draft.amount ? draft.amount / 100 : ''}
                onChange={(event) =>
                  patch({
                    amount:
                      event.target.value === '' ? 0 : Math.round(Number(event.target.value) * 100),
                  })
                }
                hint="Calculado sobre o preço do serviço, arredondando o centavo para cima."
                error={amountError}
              />
            )}
          </div>
          {draft.mode === 'DEADLINE' ? (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <Input
                  label="Pagar até após o pedido (horas)"
                  type="number"
                  min={1}
                  step="1"
                  value={hours(draft.afterRequestMinutes)}
                  onChange={(event) =>
                    patch({ afterRequestMinutes: minutesOf(event.target.value) })
                  }
                  hint="Em branco: sem esse limite."
                  error={limitError}
                />
                <Input
                  label="Pagar até antes do início (horas)"
                  type="number"
                  min={0}
                  step="1"
                  value={hours(draft.beforeStartMinutes)}
                  onChange={(event) => patch({ beforeStartMinutes: minutesOf(event.target.value) })}
                  hint="Em branco: sem esse limite."
                  error={limitError}
                />
              </div>
              <div className="space-y-2">
                <span className="block text-xs font-semibold text-content/70">
                  Ao vencer o prazo
                </span>
                <SegmentedControl
                  label="O que acontece ao vencer o prazo do sinal"
                  value={draft.expiryAction}
                  onChange={(expiryAction) => patch({ expiryAction })}
                  options={[
                    { value: 'KEEP_PENDING', label: 'Manter na fila' },
                    { value: 'CANCEL', label: 'Encerrar o pedido' },
                  ]}
                />
                <p className="text-xs text-content-faint">
                  {draft.expiryAction === 'CANCEL'
                    ? 'O sistema encerra o pedido sem o sinal pago. O que o cliente já pagou vira crédito dele, e a auditoria registra o sistema como responsável.'
                    : 'O pedido continua na fila para a recepção ajustar o prazo, dispensar o sinal ou cancelar.'}
                </p>
              </div>
            </>
          ) : null}
        </>
      ) : null}
      {error ? <RequestAlert message={error} /> : null}
      <div className="flex flex-wrap items-center justify-end gap-3">
        <FormErrorSummary form="deposit-policy-form" />
        {owned.length ? (
          <Button type="button" variant="outline" disabled={saving} onClick={removeOwn}>
            {targets.length > 1 && kinds.size > 1
              ? 'Remover regras próprias'
              : first!.kind === 'unit'
                ? 'Usar padrão da empresa'
                : 'Seguir a regra da unidade'}
          </Button>
        ) : null}
        <Button type="submit" disabled={saving}>
          {saving ? 'Salvando…' : 'Salvar sinal'}
        </Button>
      </div>
    </form>
  );
}
